import { connect } from "react-redux";

import { InvalidProgramStateError } from "../../../../../../../Common/Errors";
import {
    clearMultipleUnitInstanceAction,
    ClearPageMethod,
    copyMultipleUnitInstanceAction,
    removeMultipleUnitInstanceAction,
} from "../../../../../Engine/ActionCreators/UnitActionCreators";
import { CandyDispatch } from "../../../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../../../Helpers/StateAccessors/ViewModelSelect";
import { EvaluableProp, withEvaluators } from "../../../../withEvaluators";
import { PathResolver, withResolvePaths } from "../../../../withResolvePaths";
import { withViewModelSelect } from "../../../../withViewModelSelect";
import { SectionName, UnitId, unitIdToLegacyUnitId } from "../../UnitId/UnitId";
import { UnitActionType } from "../../Store/unitActions";
import { reject } from "../../../../../../../Common/TypingUtils";
import {
    ILazyLoadingManager,
    LazyLoadingInstanceState,
} from "../../../../../Engine/Core/LazyLoading/LazyLoadingManager";
import { AbsoluteModelPath } from "../../../../../../../Common/ModelPath/ModelPath";
import { withLazyLoadingManager } from "../../../../withLazyLoadingManager";
import { CustomNormalizationHelper } from "../../../../../Engine/Core/Normalization/CustomNormalizationHelper";
import { withCustomNormalizationHelper } from "../../../../withCustomNormalizationHelper";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { UnitIdResolver, withResolveUnitId } from "../../../../withResolveUnitId";
import { getRemoveLabelForChangeSet } from "../../../UnitItem/UnitItemHelpers";

import { ModalMultipleUnitInstance } from "./ModalMultipleUnitInstance";

export interface ModalMultipleUnitInstanceContainerProps extends FormActionHandlerExecutionInjectableProps {
    unitId: UnitId;
    viewModelSelect: ViewModelSelect;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    resolvePath: PathResolver;
    resolveUnitId: UnitIdResolver;
    lazyLoadingManager: ILazyLoadingManager;
    customNormalizationHelper: CustomNormalizationHelper;
    name?: string;
}

const mapStateToProps = (state: FullFormState, ownProps: ModalMultipleUnitInstanceContainerProps) => {
    const { unitId, viewModelSelect } = ownProps;
    const unitInfo = state.unitInfos?.find(x => x.id === unitId.pageId);
    if (unitId.instance == undefined) {
        throw new InvalidProgramStateError();
    }
    if (unitInfo == undefined) {
        throw new InvalidProgramStateError();
    }
    const bindingPath = ownProps.resolvePath(unitInfo.bindingPath);
    const bindingPathToString = bindingPath.toLegacyPath();
    if (!bindingPath.isResolved()) {
        throw new InvalidProgramStateError(`mapStateToProps bindingPath is not resolved: ${bindingPathToString}`);
    }
    const controlModel = viewModelSelect.controlModel(state, bindingPath);
    const instances = controlModel.children ?? [];

    let index: number;
    const modalUnitInfo = state.unitInfos?.find(x => x.id === unitId.pageId);
    if (modalUnitInfo?.bindingPath != undefined) {
        const modalUnitChildren = viewModelSelect.children(state, bindingPath);
        index = modalUnitChildren.indexOf(unitId.instance);
    } else {
        throw new InvalidProgramStateError();
    }

    return {
        unitId: unitId,
        bindingPath: bindingPath,
        instances: instances,
        pageActions: unitInfo.pageActions,
        hideEnableAutoValues: unitInfo.hideEnableAutoValues,
        component: unitInfo.component,
        layoutKind: unitInfo.layoutKind,
        optional: unitInfo.optional,
        index: index,
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: ModalMultipleUnitInstanceContainerProps) => ({
    onRemoveUnit: async () => {
        const { unitId, resolvePath } = ownProps;
        await dispatch(async (dispatch2, getState, form) => {
            const unitInfo = getState().unitInfos?.find(x => x.id === unitId.pageId);
            if (unitInfo == undefined || unitId.instance == undefined) {
                throw new Error("InvalidProgramState");
            }
            const resolvedBindingPath = resolvePath(unitInfo.bindingPath);
            if (!resolvedBindingPath.isResolved()) {
                throw new InvalidProgramStateError(`onRemoveUnit bindingPath is not resolved: ${resolvedBindingPath}`);
            }
            dispatch2(removeMultipleUnitInstanceAction(resolvedBindingPath, unitId.instance, unitId, false));
            await form.changeSet.send({ operationName: getRemoveLabelForChangeSet(ownProps.name) });
        });
    },

    onCopyUnit: async () => {
        const { unitId, resolvePath } = ownProps;
        await dispatch(async (_, getState) => {
            const unitInfo = getState().unitInfos?.find(x => x.id === unitId.pageId) ?? reject();
            const resolvedBindingPath = resolvePath(unitInfo.bindingPath);
            if (!resolvedBindingPath.isResolved()) {
                throw new InvalidProgramStateError(`onCopyUnit bindingPath is not resolved: ${resolvedBindingPath}`);
            }
            const nextUnitId = await dispatch(
                copyMultipleUnitInstanceAction(resolvedBindingPath, unitId.instance ?? reject(), unitId)
            );
            dispatch({
                type: UnitActionType.ReplaceModalUnit,
                payload: {
                    currentModalUnitId: unitId,
                    nextModalUnitId: nextUnitId,
                },
            });
        });
    },

    onClearUnit: (_: string, method: ClearPageMethod) => {
        const { unitId, resolveUnitId, resolvePath } = ownProps;
        dispatch((nextDispatch, getState) => {
            const unitInfo = getState().unitInfos?.find(x => x.id === unitId.pageId);
            if (unitInfo == undefined || unitId.instance == undefined) {
                throw new Error("InvalidProgramState");
            }
            const resolvedBindingPath = resolvePath(unitInfo.bindingPath);
            if (!resolvedBindingPath.isResolved()) {
                throw new InvalidProgramStateError(`onClearUnit bindingPath is not resolved: ${resolvedBindingPath}`);
            }
            nextDispatch(
                clearMultipleUnitInstanceAction(
                    unitIdToLegacyUnitId(resolveUnitId(unitId)) as SectionName,
                    resolvedBindingPath,
                    unitId.instance,
                    method
                )
            );
        });
    },

    onCheckModalUnitDataLoaded: async (bindingPath: AbsoluteModelPath, instance: string) => {
        if (bindingPath != undefined && instance != undefined) {
            const lazyLoadingManager = ownProps.lazyLoadingManager;
            if (
                lazyLoadingManager.isLazyLoadingEnabled() &&
                lazyLoadingManager.getInstanceLazyLoadingState(bindingPath, instance) !==
                    LazyLoadingInstanceState.FullyLoaded
            ) {
                await lazyLoadingManager.requestFullInstanceLoad(bindingPath, instance);
            }
        }
    },
    onMount: async () => {
        const { unitId } = ownProps;
        await dispatch(async (_, getState) => {
            const unitInfo = getState().unitInfos?.find(x => x.id === unitId.pageId);
            if (unitInfo == undefined || unitId.instance == undefined) {
                throw new Error("InvalidProgramState");
            }
            if (unitInfo.onMount != undefined) {
                await ownProps.executeFormActionHandler(unitInfo.onMount);
            }
        });
    },
    onUnmount: async () => {
        const { unitId } = ownProps;
        await dispatch(async (_, getState) => {
            const unitInfo = getState().unitInfos?.find(x => x.id === unitId.pageId);
            if (unitInfo == undefined || unitId.instance == undefined) {
                throw new Error("InvalidProgramState");
            }
            if (unitInfo.onUnmount != undefined) {
                await ownProps.executeFormActionHandler(unitInfo.onUnmount);
            }
        });
    },
});

export const ModalMultipleUnitInstanceContainer = withResolvePaths(
    withResolveUnitId(
        withViewModelSelect(
            withLazyLoadingManager(
                withCustomNormalizationHelper(
                    withEvaluators(
                        withFormActionHandlerExecution(
                            connect(mapStateToProps, mapDispatchToProps)(ModalMultipleUnitInstance)
                        )
                    )
                )
            )
        )
    )
);
